import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import ActivityRow from "@/components/ActivityRow";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Activity as ActivityIcon, Loader2, Search, Download, X, User } from "lucide-react";

const PAGE = 30;
const ACTIONS = ["create", "update", "status_change", "assign", "upload", "bulk_update", "delete", "activate", "deactivate", "purge"];
const ENTITIES = ["job", "customer", "user", "photo", "database"];

export default function Activity() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [q, setQ] = useState("");
  const [action, setAction] = useState("all");
  const [entity, setEntity] = useState("all");
  const [actor, setActor] = useState(null); // { id, name }
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const buildParams = useCallback(() => {
    const p = {};
    if (q) p.q = q;
    if (action !== "all") p.action = action;
    if (entity !== "all") p.entity = entity;
    if (actor) p.actor = actor.id;
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return p;
  }, [q, action, entity, actor, dateFrom, dateTo]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/audit", { params: { ...buildParams(), limit: PAGE, skip } });
      setLogs(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      console.error("Failed to load activity log:", e);
      toast.error("Couldn't load the activity log. Please try again.");
    }
    setLoading(false);
  }, [skip, buildParams]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSkip(0); }, [q, action, entity, actor, dateFrom, dateTo]);

  const onActorClick = (id, name) => setActor({ id, name });

  const doExport = async (format) => {
    setExporting(true);
    try {
      const r = await api.get("/audit/export", { params: { ...buildParams(), format }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `reshine-activity.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Activity log exported as ${format.toUpperCase()}`);
    } catch {
      toast.error("Export failed. Please try again.");
    }
    setExporting(false);
  };

  const from = total === 0 ? 0 : skip + 1;
  const to = Math.min(skip + PAGE, total);

  return (
    <div className="space-y-6" data-testid="activity-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
            <ActivityIcon className="h-3.5 w-3.5" /> Audit Trail
          </p>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-1">Activity Log</h1>
          <p className="text-sm text-muted-foreground mt-1">Complete record of every action taken across the system.</p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="gap-2" disabled={exporting || total === 0} data-testid="activity-export-button">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => doExport("csv")} data-testid="activity-export-csv">Export as CSV</DropdownMenuItem>
            <DropdownMenuItem onClick={() => doExport("pdf")} data-testid="activity-export-pdf">Export as PDF</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="activity-search-input" placeholder="Search people or details…" value={q}
            onChange={(e) => setQ(e.target.value)} className="pl-9" />
        </div>
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger className="sm:w-44" data-testid="activity-action-filter"><SelectValue placeholder="Action" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            {ACTIONS.map((a) => <SelectItem key={a} value={a}>{a.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={entity} onValueChange={setEntity}>
          <SelectTrigger className="sm:w-40" data-testid="activity-entity-filter"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {ENTITIES.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground">Date range:</span>
        <Input type="date" value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)}
          className="w-auto h-9" data-testid="activity-date-from" />
        <span className="text-xs text-muted-foreground">to</span>
        <Input type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)}
          className="w-auto h-9" data-testid="activity-date-to" />
        {(dateFrom || dateTo) && (
          <Button variant="ghost" size="sm" className="h-9 gap-1 text-muted-foreground"
            onClick={() => { setDateFrom(""); setDateTo(""); }} data-testid="activity-clear-dates">
            <X className="h-3.5 w-3.5" /> Clear dates
          </Button>
        )}
      </div>

      {actor && (
        <div className="flex items-center gap-2" data-testid="activity-person-chip">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary border border-primary/30 px-3 py-1 text-xs font-semibold">
            <User className="h-3 w-3" /> {actor.name}
            <button onClick={() => setActor(null)} data-testid="activity-clear-person" className="ml-1 hover:text-primary/70">
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
          <span className="text-xs text-muted-foreground">Showing only this person's activity</span>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : logs.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-sm text-muted-foreground">No activity found for these filters.</CardContent></Card>
      ) : (
        <div className="space-y-2" data-testid="activity-list">
          {logs.map((log) => <ActivityRow key={log.id} log={log} onActorClick={onActorClick} />)}
        </div>
      )}

      <div className="flex items-center justify-between pt-2">
        <p className="text-xs text-muted-foreground" data-testid="activity-range">
          {from}–{to} of {total}
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={skip === 0 || loading}
            onClick={() => setSkip(Math.max(0, skip - PAGE))} data-testid="activity-prev-button">Previous</Button>
          <Button variant="outline" size="sm" disabled={to >= total || loading}
            onClick={() => setSkip(skip + PAGE)} data-testid="activity-next-button">Next</Button>
        </div>
      </div>
    </div>
  );
}
